import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Truck, ExternalLink } from "lucide-react";
import { getCheckoutStatus, getOrder } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { KNOW_IT_OWLS_LOGO } from "@/data/products";
import { PixelBurst } from "@/components/PixelBurst";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order");
  const [state, setState] = useState({ kind: "polling", message: "Confirming payment…" });
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  // Fetch order (with easyship sub-doc) once we know we have an order id.
  // Poll for up to ~12s so the background shipment-creation task has time to
  // populate tracking info before we paint the screen.
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const fetchOrder = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const o = await getOrder(orderId);
        setOrder(o);
        if (!o?.easyship?.tracking_url && attempt < 6) {
          setTimeout(() => fetchOrder(attempt + 1), 2000);
        }
      } catch (_) {
        if (attempt < 4) setTimeout(() => fetchOrder(attempt + 1), 2000);
      }
    };
    fetchOrder();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "error", message: "Missing session id." });
      return;
    }
    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState({
          kind: "timeout",
          message: "Still confirming with Stripe — check your email shortly.",
        });
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          setState({ kind: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ kind: "expired", message: "This checkout session expired." });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="success-page"
      className="min-h-screen grid-paper flex items-center justify-center px-4 py-16"
    >
      <div className="max-w-xl w-full border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal p-8">
        <img src={KNOW_IT_OWLS_LOGO} alt="The Know It Owls" className="h-20 mx-auto" />
        {state.kind === "polling" && (
          <div className="text-center mt-6">
            <Loader2 size={32} className="mx-auto animate-spin text-[var(--st-maroon)]" />
            <p className="mt-3 font-display uppercase text-2xl text-[var(--st-green)]">
              {state.message}
            </p>
          </div>
        )}
        {state.kind === "paid" && (
          <div className="text-center mt-6 relative">
            {/* Celebratory pixel burst from the centre when payment confirmed. */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative" style={{ width: 1, height: 1 }}>
                <PixelBurst origin="center" count={60} spread={[160, 420]} size={[8, 22]} baseDelay={0.1} zIndex={5} />
              </div>
            </div>
            <CheckCircle2 size={40} className="mx-auto text-[var(--st-maroon)]" />
            <p className="mt-3 font-display uppercase text-3xl text-[var(--st-green)]">
              You&apos;re enrolled!
            </p>
            <p className="mt-2 font-body text-sm text-[var(--st-fg)]/80">
              Payment confirmed. Your gear is on its way. Welcome to The Know It Owls.
            </p>
            <p
              data-testid="success-amount"
              className="mt-4 font-price text-4xl text-[var(--st-maroon)] leading-none"
            >
              ${state.data.amount_total.toFixed(2)}{" "}
              <span className="font-heading text-base align-middle tracking-[0.18em]">
                {state.data.currency.toUpperCase()}
              </span>
            </p>

            {/* TRACKING — appears once the background Easyship task lands */}
            {order?.easyship && (order.easyship.tracking_url || order.easyship.tracking_number) && (
              <div
                data-testid="success-tracking"
                className="mt-6 border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal-sm p-4 text-left"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Truck size={16} className="text-[var(--st-maroon)]" strokeWidth={2.5} />
                  <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
                    Shipment {order.order_number}
                  </p>
                </div>
                {order.easyship.tracking_number && (
                  <p className="font-body text-sm">
                    <span className="text-[var(--st-fg)]/70">Tracking #:</span>{" "}
                    <span className="font-mono">{order.easyship.tracking_number}</span>
                  </p>
                )}
                {order.easyship.tracking_url && (
                  <a
                    href={order.easyship.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="success-tracking-link"
                    className="mt-3 inline-flex items-center gap-1.5 border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] px-3 py-2 font-heading uppercase tracking-[0.18em] text-xs"
                  >
                    Track your order <ExternalLink size={12} />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        {(state.kind === "expired" || state.kind === "error" || state.kind === "timeout") && (
          <div className="text-center mt-6">
            <AlertTriangle size={32} className="mx-auto text-[var(--st-orange)]" />
            <p className="mt-3 font-display uppercase text-2xl text-[var(--st-green)]">
              {state.message}
            </p>
          </div>
        )}
        <div className="mt-8 flex justify-center">
          <Link to="/" data-testid="back-home" className="btn-brutal">
            Back to Class
          </Link>
        </div>
      </div>
    </div>
  );
}
